import ApiService from '@/services/api.service';
import StorageService from '@/services/storage.service';
import {
  AuthError, TooManyAttemptsError, ValidationError, PMSError,
} from '@/errors';
import { appEndpoint, apiEndpoint } from '@/shared';

function handleApiError(error) {
  const { status, data } = error.response;
  const rlr = error.response.headers['x-ratelimit-reset'] || 0;
  switch (status) {
    case 401:
      // TODO: remove user from storage?
      return new AuthError(status, data.message);
    case 409: // PMSError
      return new PMSError(status, data.message);
    case 422:
      return new ValidationError(status, data.message, data.errors);
    case 429:
      return new TooManyAttemptsError(rlr ? data.message : data.errors.email[0], rlr);
    default:
      return error;
  }
}

const UserService = {
  async getCookie() {
    try {
      ApiService.setHeader(StorageService.getToken());
    } catch (error) {
      // FIXME
      // do nothing
    }
  },

  async login(email, password) {
    try {
      // now we can make login request
      const result = await ApiService.post(`${appEndpoint}/login`, {
        email,
        password,
      });
      ApiService.setHeader(result.data.data.access_token);
      // fetch user after successful login
      StorageService.setUser(result.data.data.user);
      StorageService.setTokenExpireTime(result.data.data.expiresIn);
      StorageService.setToken(result.data.data.access_token);
      return result.data.data.user;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async logout(forced = false) {
    if (!forced) {
      try {
        await ApiService.post(`${appEndpoint}/logout`);
      } catch (e) {
        // do nothing
      }
    }
    StorageService.removeUser();
    ApiService.disableAuthErrorInterceptor();
  },

  async getUser() {
    try {
      return StorageService.getUser();
    } catch (error) {
      StorageService.removeUser();
      throw new AuthError(error.response.status, error.response.data.message);
    }
  },

  async resendEmail() {
    try {
      await ApiService.post(`${appEndpoint}/email/resend`);
      return true;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async verifyEmail(id, hash, query) {
    try {
      // const queryParams = (new URLSearchParams(query)).toString();
      const url = `${appEndpoint}/email/verify/${id}/${hash}`;
      const response = await ApiService.post(url, query);
      let user;
      if (response.status === 204) {
        // already verified
        user = StorageService.getUser();
        user.email_verified = true;
      } else {
        user = response.data;
      }
      StorageService.setUser(user);
      return user;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateProfile(data) {
    try {
      const url = `${apiEndpoint}/user/profile`;
      const response = await ApiService.post(url, data);
      const user = response.data;
      StorageService.setUser(user);
      return user;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  async updateProfileData(data) {
    try {
      const url = `${apiEndpoint}/user`;
      const response = await ApiService.put(url, data);
      const user = response.data;
      StorageService.setUser(user);
      return user;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  async sendResetPasswordEmail(email) {
    try {
      const url = `${appEndpoint}/forgot-password`;
      await ApiService.post(url, { email });
    } catch (error) {
      throw handleApiError(error);
    }
    return true;
  },

  async sendChangeEmail() {
    try {
      const email = 'emm';
      const url = `${appEndpoint}/email/send-email`;
      await ApiService.post(url, { email });
    } catch (error) {
      throw handleApiError(error);
    }
    return true;
  },

  async changeEmail(email, token, newEmail) {
    try {
      const url = `${appEndpoint}/email/update`;
      await ApiService.post(url, {
        email,
        token,
        newEmail,
      });
    } catch (error) {
      throw handleApiError(error);
    }
    return true;
  },

  async resetPassword(data) {
    try {
      const url = `${appEndpoint}/reset-password`;
      await ApiService.post(url, data);
    } catch (error) {
      throw handleApiError(error);
    }
    return true;
  },

  // eslint-disable-next-line camelcase
  async register(email, password, tos_agreed) {
    try {
      await this.getCookie();

      const url = `${appEndpoint}/register`;
      await ApiService.post(url, {
        email,
        password,
        tos_agreed,
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async setupStep(payload) {
    try {
      const url = `${apiEndpoint}/user/setup`;
      const { data } = await ApiService.patch(url, payload);
      return data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default UserService;
